import apiFetch from '../axios/config';

const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const getErrorDetail = (error) => {
  if (error.response?.data?.detail) {
    return typeof error.response.data.detail === 'string'
      ? error.response.data.detail
      : JSON.stringify(error.response.data.detail);
  }
  return error.message || 'Erro ao processar requisição';
};

/**
 * Lista credenciais do usuário (paginado).
 * @param {string} token - JWT do usuário (Cognito)
 * @param {{ page?: number, maxResults?: number }} options - page (1-based), maxResults (máx 50)
 * @returns {Promise<{ credentials: Array, page_number: number, total_pages: number, total_results: number, results_per_page: number }>}
 */
export async function listCredentials(token, { page, maxResults } = {}) {
  const params = {};
  if (page != null) params.page = page;
  if (maxResults != null) params.max_results = maxResults;
  try {
    const response = await apiFetch.get('/credentials', {
      headers: getAuthHeaders(token),
      params,
    });
    return response.data;
  } catch (error) {
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}

/**
 * Obtém uma credencial pelo credential_key.
 * @param {string} token - JWT do usuário
 * @param {string} credentialKey - UUID da credencial
 * @returns {Promise<{ credential: object }>}
 */
export async function getCredential(token, credentialKey) {
  try {
    const response = await apiFetch.get('/credentials', {
      headers: getAuthHeaders(token),
      params: { credential_key: credentialKey },
    });
    return response.data;
  } catch (error) {
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}

/**
 * Cria uma nova credencial (PRIVATE ou ICP).
 * @param {string} token - JWT do usuário
 * @param {object} body - { credential_type, template_key, automatic_signature?, terms_acceptance: { accepted_text, evidence }, certificate?, certificate_password? }
 * @returns {Promise<{ credential: object }>}
 */
export async function createCredential(token, body) {
  try {
    const response = await apiFetch.post('/credentials', body, {
      headers: getAuthHeaders(token),
    });
    return response.data;
  } catch (error) {
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}

/**
 * Atualiza uma credencial (PATCH).
 * @param {string} token - JWT do usuário
 * @param {string} credentialKey - UUID da credencial
 * @param {object} body - { automatic_signature?, due_date?, template_key? }
 * @returns {Promise<{ credential: object }>}
 */
export async function updateCredential(token, credentialKey, body) {
  try {
    const response = await apiFetch.patch(`/credentials/${credentialKey}`, body, {
      headers: getAuthHeaders(token),
    });
    return response.data;
  } catch (error) {
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}

/**
 * Busca informações públicas de um template pelo template_key.
 * Usado para validar um código de convite externo antes de criar uma credencial.
 * @param {string} token - JWT do usuário
 * @param {string} templateKey - UUID do template
 * @returns {Promise<{ template_name: string, entity_name: string }>}
 */
export async function lookupExternalTemplate(token, templateKey) {
  try {
    const response = await apiFetch.get(`/credentials/templates/${templateKey}/info`, {
      headers: getAuthHeaders(token),
    });
    return response.data;
  } catch (error) {
    const status = error.response?.status;
    if (status === 404) throw new Error('Código não encontrado. Verifique se você copiou corretamente.');
    if (status === 403) throw new Error('Este template não está disponível para uso externo.');
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}

/**
 * Desativa uma credencial (soft delete).
 * @param {string} token - JWT do usuário
 * @param {string} credentialKey - UUID da credencial
 * @returns {Promise<{ message: string }>}
 */
export async function deleteCredential(token, credentialKey) {
  try {
    const response = await apiFetch.delete(`/credentials/${credentialKey}`, {
      headers: getAuthHeaders(token),
    });
    return response.data;
  } catch (error) {
    const detail = getErrorDetail(error);
    throw new Error(detail);
  }
}
